import React from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from '../ui/Button';
import { Checkbox, SelectField, TextArea, TextField } from '../ui/Field';

/* ===================== TYPES ===================== */

export type BillingInterval = "day" | "week" | "month" | "year";

export type PricingPlanDraft = {
  id: string;
  planKey: string;
  currency: string;
  amountMajor: string;
  billingMode: "SUBSCRIPTION";
  displayName: string;
  description: string;
  interval: BillingInterval;
  intervalCount: string;
};

export function emptyPricingPlanDraft(): PricingPlanDraft {
  return {
    id: uuidv4(),
    planKey: "",
    currency: "USD",
    amountMajor: "",
    billingMode: "SUBSCRIPTION",
    displayName: "",
    description: "",
    interval: "month",
    intervalCount: "1",
  };
}

export function draftFromApiPlan(plan: Record<string, unknown>): PricingPlanDraft {
  const amountMinor =
    typeof plan.amountMinor === "number" && Number.isFinite(plan.amountMinor)
      ? plan.amountMinor
      : 0;
  const intervalRaw = plan.interval;
  const interval: BillingInterval =
    intervalRaw === "day" ||
    intervalRaw === "week" ||
    intervalRaw === "month" ||
    intervalRaw === "year"
      ? intervalRaw
      : "month";
  return {
    id: uuidv4(),
    planKey: typeof plan.planKey === "string" ? plan.planKey : "",
    currency: typeof plan.currency === "string" ? plan.currency.toUpperCase() : "USD",
    amountMajor: amountMinor > 0 ? (amountMinor / 100).toFixed(2) : "",
    billingMode: "SUBSCRIPTION",
    displayName: typeof plan.displayName === "string" ? plan.displayName : "",
    description: typeof plan.description === "string" ? plan.description : "",
    interval,
    intervalCount:
      typeof plan.intervalCount === "number" && plan.intervalCount > 0
        ? String(plan.intervalCount)
        : "1",
  };
}

export function serializePlanDraft(p: PricingPlanDraft): Record<string, unknown> | null {
  const planKey = p.planKey.trim();
  const displayName = p.displayName.trim();
  const currency = p.currency.trim().toUpperCase();
  const amount = parseFloat(p.amountMajor);
  if (!planKey || !displayName || currency.length !== 3 || !Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  const amountMinor = Math.round(amount * 100);
  if (amountMinor < 1) return null;

  const ic = parseInt(p.intervalCount, 10);
  if (!p.interval || !Number.isFinite(ic) || ic < 1) return null;

  const base: Record<string, unknown> = {
    planKey,
    currency,
    amountMinor,
    billingMode: "SUBSCRIPTION",
    displayName,
    interval: p.interval,
    intervalCount: ic,
  };
  const desc = p.description.trim();
  if (desc) base.description = desc;

  return base;
}

/* ===================== COMPONENT ===================== */

type Props = {
  pricingFree: boolean;
  pricingPlans: PricingPlanDraft[];
  onFreeChange: (nextFree: boolean) => void;
  onPlansChange: (next: PricingPlanDraft[]) => void;
  /** e.g. "agent type" or "AI pod" — used in the free-toggle label */
  itemLabel?: string;
  /** Where the pricing block is persisted, shown in the helper text. */
  metadataHint?: string;
};

export const PricingPlansEditor: React.FC<Props> = ({
  pricingFree,
  pricingPlans,
  onFreeChange,
  onPlansChange,
  itemLabel = "item",
  metadataHint = "metadata.pricing",
}) => {
  const patchPlan = (id: string, patch: Partial<PricingPlanDraft>) => {
    onPlansChange(pricingPlans.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const removePlan = (id: string) => {
    if (!window.confirm("Remove this plan?")) return;
    onPlansChange(pricingPlans.filter((p) => p.id !== id));
  };

  return (
    <section className="space-y-3 border-t border-gray-200 pt-4">
      <h3 className="font-semibold text-base">Pricing</h3>
      <p className="text-xs text-gray-500">
        Written to <span className="font-mono text-gray-700">{metadataHint}</span>.
        Uncheck below to add subscription plans.
      </p>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox
         
          checked={pricingFree}
          onChange={(e) => onFreeChange(e.target.checked)}
        />
        Free {itemLabel} (default: no pricing)
      </label>

      {!pricingFree && (
        <div className="space-y-4">
          {pricingPlans.map((plan, idx) => (
            <div key={plan.id} className="border rounded-lg p-4 space-y-3 bg-gray-50/50">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-sm">Plan {idx + 1}</h4>
                <Button tone="danger-text"
                  type="button"
                  className="text-sm"
                  onClick={() => removePlan(plan.id)}
                >
                  Remove plan
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PlanInput
                  label="Plan key"
                  value={plan.planKey}
                  onChange={(v) => patchPlan(plan.id, { planKey: v })}
                />
                <PlanInput
                  label="Display name"
                  value={plan.displayName}
                  onChange={(v) => patchPlan(plan.id, { displayName: v })}
                />
                <PlanInput
                  label="Currency (ISO 4217)"
                  value={plan.currency}
                  onChange={(v) => patchPlan(plan.id, { currency: v.toUpperCase().slice(0, 3) })}
                />
                <PlanInput
                  label="Price (major units, e.g. 499.00)"
                  value={plan.amountMajor}
                  onChange={(v) => patchPlan(plan.id, { amountMajor: v })}
                />
              </div>
              <p className="text-sm text-gray-600">
                Billing: <span className="font-medium text-gray-800">Subscription</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Billing interval</label>
                  <SelectField
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={plan.interval}
                    onChange={(e) =>
                      patchPlan(plan.id, {
                        interval: e.target.value as BillingInterval,
                      })
                    }
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                  </SelectField>
                </div>
                <PlanInput
                  label="Interval count"
                  value={plan.intervalCount}
                  onChange={(v) => patchPlan(plan.id, { intervalCount: v })}
                />
              </div>
              <PlanTextarea
                label="Description (optional)"
                value={plan.description}
                onChange={(v) => patchPlan(plan.id, { description: v })}
              />
            </div>
          ))}
          <Button tone="custom"
            type="button"
            onClick={() => onPlansChange([...pricingPlans, emptyPricingPlanDraft()])}
            className="border border-dashed px-4 py-2 rounded text-sm"
          >
            + Add plan
          </Button>
        </div>
      )}
    </section>
  );
};

/* ===================== PRIMITIVES ===================== */

const PlanInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <TextField
      className="w-full border px-3 py-2 rounded"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const PlanTextarea = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <TextArea
      className="w-full border px-3 py-2 rounded"
      rows={3}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default PricingPlansEditor;
